import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  PRINT_JOBS_QUEUED_EVENT,
  claimDevicePrintJobs,
  getMyDevice,
  reportDevicePrintJob,
} from '../lib/api';
import { DEVICE_IDENTITY_EVENT, readDeviceIdentity } from '../lib/deviceIdentity';
import { executePrintJob, printerNeedsAgent } from '../lib/printTransports';
import { getAgentHealth } from '../lib/printTransports/agent';

// Runs on every registered device: picks up the print jobs routed to this device's
// printers and prints them locally (agent, Epson ePOS or Web Bluetooth). It runs
// independent of the waiter login, so a Bar-PC keeps printing kitchen tickets
// while nobody is signed in there.

const POLL_INTERVAL_MS = 2000;
const DEVICE_REFRESH_MS = 60000;
const AGENT_CHECK_MS = 15000;
const RECENT_JOBS_LIMIT = 10;

const PrintJobContext = createContext(null);

export function PrintJobProvider({ children }) {
  const [identity, setIdentity] = useState(() => readDeviceIdentity());
  const [device, setDevice] = useState(null);
  const [deviceRevoked, setDeviceRevoked] = useState(false);
  const [agentHealth, setAgentHealth] = useState(null);
  const [agentChecked, setAgentChecked] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const busyRef = useRef(false);

  const printers = useMemo(() => device?.printers || [], [device]);
  const needsAgent = printers.some(printerNeedsAgent);

  useEffect(() => {
    const syncIdentity = () => {
      setIdentity(readDeviceIdentity());
      setDeviceRevoked(false);
    };
    window.addEventListener(DEVICE_IDENTITY_EVENT, syncIdentity);
    window.addEventListener('storage', syncIdentity);
    return () => {
      window.removeEventListener(DEVICE_IDENTITY_EVENT, syncIdentity);
      window.removeEventListener('storage', syncIdentity);
    };
  }, []);

  const refreshDevice = useCallback(async () => {
    if (!readDeviceIdentity()) {
      setDevice(null);
      return null;
    }
    try {
      const data = await getMyDevice();
      setDevice(data);
      setDeviceRevoked(false);
      return data;
    } catch (error) {
      if (error?.response?.status === 401) setDeviceRevoked(true);
      return null;
    }
  }, []);

  const checkAgent = useCallback(async () => {
    const health = await getAgentHealth();
    setAgentHealth(health);
    setAgentChecked(true);
    return health;
  }, []);

  const recordJob = useCallback((job, status, error = '') => {
    setRecentJobs((current) => [
      { id: job.id, title: job.title, printerName: job.printer?.name || 'Drucker', status, error, at: new Date().toISOString() },
      ...current,
    ].slice(0, RECENT_JOBS_LIMIT));
  }, []);

  const pollNow = useCallback(async () => {
    if (!readDeviceIdentity() || busyRef.current) return;
    busyRef.current = true;
    try {
      const { jobs = [] } = await claimDevicePrintJobs();
      for (const job of jobs) {
        try {
          await executePrintJob(job);
          await reportDevicePrintJob(job.id, 'done');
          recordJob(job, 'done');
        } catch (error) {
          const message = error?.message || 'Druck fehlgeschlagen';
          recordJob(job, 'failed', message);
          toast.error(`${job.printer?.name || 'Drucker'}: ${message}`);
          await reportDevicePrintJob(job.id, 'failed', message).catch(() => {});
        }
      }
    } catch (error) {
      if (error?.response?.status === 401) setDeviceRevoked(true);
    } finally {
      busyRef.current = false;
    }
  }, [recordJob]);

  useEffect(() => {
    if (!identity) {
      setDevice(null);
      return undefined;
    }
    refreshDevice();
    const timer = setInterval(refreshDevice, DEVICE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [identity, refreshDevice]);

  useEffect(() => {
    if (!identity || deviceRevoked) return undefined;
    pollNow();
    const timer = setInterval(pollNow, POLL_INTERVAL_MS);
    window.addEventListener(PRINT_JOBS_QUEUED_EVENT, pollNow);
    return () => {
      clearInterval(timer);
      window.removeEventListener(PRINT_JOBS_QUEUED_EVENT, pollNow);
    };
  }, [identity, deviceRevoked, pollNow]);

  // Only devices with agent printers probe localhost, so phones never get a
  // "local network access" permission prompt for an agent they don't have.
  useEffect(() => {
    if (!identity || !needsAgent) return undefined;
    checkAgent();
    const timer = setInterval(checkAgent, AGENT_CHECK_MS);
    return () => clearInterval(timer);
  }, [identity, needsAgent, checkAgent]);

  const value = useMemo(() => ({
    identity,
    device,
    printers,
    deviceRevoked,
    needsAgent,
    agentHealth,
    agentChecked,
    recentJobs,
    pollNow,
    refreshDevice,
    checkAgent,
  }), [identity, device, printers, deviceRevoked, needsAgent, agentHealth, agentChecked, recentJobs, pollNow, refreshDevice, checkAgent]);

  return <PrintJobContext.Provider value={value}>{children}</PrintJobContext.Provider>;
}

export function usePrintJobs() {
  const context = useContext(PrintJobContext);
  if (!context) throw new Error('usePrintJobs must be used inside PrintJobProvider');
  return context;
}
